import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// ---------------------------------------------------------------------------
// Mesmo padrão visual usado em exportarPrevistoRealizadoExcel.js
// ---------------------------------------------------------------------------

const NOMES_MESES = [
    "JANEIRO", "FEVEREIRO", "MARCO", "ABRIL", "MAIO", "JUNHO",
    "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO",
];

const FORMATO_MOEDA = '#,##0.00;[Red]-#,##0.00';
const FORMATO_PERCENTUAL = '0.00"%";[Red]-0.00"%"'; // assume que o back-end já manda o valor multiplicado (ex.: 15.32 = 15,32%)

const CORES = {
    fixo: { forte: "FF2C3E50" },
    mes: { forte: "FF1F4E79", clara: "FF2E75B6" },
    acumulado: { forte: "FF7B68EE", clara: "FF9370DB", linha: "FFE6E6FA" },
    total: { forte: "FFC45911", clara: "FFED7D31", linha: "FFFFF2CC" },
    linhaFixa: "FFE8F4FD",
    totalGeral: "FFD9E2F3",
    bordaClara: "FFB7C6D9",
    positivo: "FF1E7A34",
    negativo: "FFC0392B",
    cardFundo: "FFF4F7FB",
    cardTitulo: "FF5B84A8",
};

function estiloCabecalho(cor, tamanho = 12) {
    return {
        font: { name: "Calibri", size: tamanho, bold: true, color: { argb: "FFFFFFFF" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: cor } },
        alignment: { horizontal: "center", vertical: "middle", wrapText: true },
    };
}

function bordaFina(cor = CORES.bordaClara) {
    return { style: "thin", color: { argb: cor } };
}

function n(v) {
    return typeof v === "number" ? v : Number(v || 0);
}

// =============================================================================
// ABA 1 - DASHBOARD (BpKpiDTO + BpRelatorioTotaisDTO)
// =============================================================================

function criarAbaDashboard(workbook, bpKpiDTO = {}, totais = {}, ano) {
    const ws = workbook.addWorksheet("Dashboard", {
        views: [{ showGridLines: false }],
    });

    ws.columns = [
        { width: 3 }, { width: 24 }, { width: 18 }, { width: 3 },
        { width: 24 }, { width: 18 }, { width: 3 }, { width: 24 }, { width: 18 },
    ];

    // ---------- Título ----------
    ws.mergeCells(1, 1, 1, 9);
    const titulo = ws.getCell(1, 1);
    titulo.value = `Dashboard BP - ${ano}`;
    titulo.style = estiloCabecalho(CORES.fixo.forte, 16);
    ws.getRow(1).height = 32;

    // ---------- Cards de KPI ----------
    const cards = [
        { label: "Total Orçado", valor: bpKpiDTO.totalOrcado, tipo: "moeda" },
        { label: "Total Realizado", valor: bpKpiDTO.totalRealizado, tipo: "moeda" },
        { label: "Saldo", valor: bpKpiDTO.saldo, tipo: "moeda", cor: true },
        { label: "% Realizado", valor: bpKpiDTO.porcentagemRealizado, tipo: "percentual" },
        { label: "Tendência", valor: bpKpiDTO.tendencia, tipo: "moeda" },
        { label: "Média Orçado/mês", valor: bpKpiDTO.mediaOrcado, tipo: "moeda" },
        { label: "Média Realizado/mês", valor: bpKpiDTO.mediaRealizado, tipo: "moeda" },
        { label: "Economia", valor: bpKpiDTO.economia, tipo: "moeda", cor: true },
        { label: "% Economia", valor: bpKpiDTO.porcentagemEconomia, tipo: "percentual", cor: true },
        { label: "Lucro Anual", valor: bpKpiDTO.lucroAnual, tipo: "moeda", cor: true },
        { label: "% Lucro Anual", valor: bpKpiDTO.porcentagemLucroAnual, tipo: "percentual", cor: true },
        { label: "Forecast Anual", valor: bpKpiDTO.forecastAnual, tipo: "moeda" },
        { label: "Forecast Falta", valor: bpKpiDTO.forecastFalta, tipo: "moeda" },
    ];

    const colunasCard = [2, 5, 8]; // B, E, H
    let linhaCard = 3;
    cards.forEach((card, i) => {
        const col = colunasCard[i % 3];
        if (i > 0 && i % 3 === 0) linhaCard += 3;

        ws.mergeCells(linhaCard, col, linhaCard, col + 1);
        const label = ws.getCell(linhaCard, col);
        label.value = card.label;
        label.font = { name: "Calibri", size: 9, bold: true, color: { argb: "FFFFFFFF" } };
        label.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.cardTitulo } };
        label.alignment = { horizontal: "center", vertical: "middle" };

        ws.mergeCells(linhaCard + 1, col, linhaCard + 1, col + 1);
        const valorCell = ws.getCell(linhaCard + 1, col);
        const valor = n(card.valor);
        valorCell.value = valor;
        valorCell.numFmt = card.tipo === "percentual" ? FORMATO_PERCENTUAL : FORMATO_MOEDA;
        valorCell.font = {
            name: "Calibri", size: 13, bold: true,
            color: { argb: card.cor ? (valor >= 0 ? CORES.positivo : CORES.negativo) : "FF2C3E50" },
        };
        valorCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.cardFundo } };
        valorCell.alignment = { horizontal: "center", vertical: "middle" };
        ws.getRow(linhaCard + 1).height = 22;
    });
    linhaCard += 3;

    // ---------- Tabela genérica "chave -> orçado/realizado/anterior/saldo" ----------
    const renderTabelaTotais = (linhaInicio, titulo, mapaOrcado = {}, mapaRealizado = {}, mapaAnterior = {}) => {
        const chaves = Array.from(new Set([
            ...Object.keys(mapaOrcado || {}),
            ...Object.keys(mapaRealizado || {}),
            ...Object.keys(mapaAnterior || {}),
        ])).sort();

        if (chaves.length === 0) return linhaInicio;

        ws.mergeCells(linhaInicio, 2, linhaInicio, 8);
        const th = ws.getCell(linhaInicio, 2);
        th.value = titulo;
        th.style = estiloCabecalho(CORES.mes.forte, 11);
        ws.getRow(linhaInicio).height = 20;

        const cabecalhos = ["", "Orçado", "Realizado", "Realizado (ano anterior)", "Saldo"];
        const larguraColunas = [3, 1, 1, 2, 1]; // relativo (span em colunas B..H)
        let r = linhaInicio + 1;
        ["Item", "Orçado", "Realizado", "Ano Anterior", "Saldo"].forEach((h, i) => {
            const c = ws.getCell(r, 2 + i);
            c.value = h;
            c.style = estiloCabecalho(CORES.mes.clara, 9);
        });
        r += 1;

        chaves.forEach((chave, idx) => {
            const orc = n(mapaOrcado[chave]);
            const rea = n(mapaRealizado[chave]);
            const ant = n(mapaAnterior[chave]);
            const saldo = orc - rea;

            const linha = ws.getRow(r);
            linha.getCell(2).value = chave;
            linha.getCell(3).value = orc;
            linha.getCell(4).value = rea;
            linha.getCell(5).value = ant;
            linha.getCell(6).value = saldo;

            [3, 4, 5, 6].forEach((c) => { linha.getCell(c).numFmt = FORMATO_MOEDA; });
            linha.getCell(6).font = { name: "Calibri", size: 9, bold: true, color: { argb: saldo >= 0 ? CORES.positivo : CORES.negativo } };

            for (let c = 2; c <= 6; c++) {
                const cell = linha.getCell(c);
                cell.font = cell.font || { name: "Calibri", size: 9 };
                cell.alignment = { horizontal: c === 2 ? "left" : "right", vertical: "middle" };
                if (idx % 2 === 0) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.linhaFixa } };
            }
            r += 1;
        });

        return r + 1; // linha em branco de respiro
    };

    linhaCard = renderTabelaTotais(linhaCard, "Totais Mensais", totais.totalOrcadoMensal, totais.totalRealizadoMensal, totais.totalRealizadoMensalAnterior);
    linhaCard = renderTabelaTotais(linhaCard, "Totais por Centro de Custo", totais.totalOrcadoPorCentroCusto, totais.totalRealizadoPorCentroCusto, totais.totalRealizadoPorCentroCustoAnterior);
    linhaCard = renderTabelaTotais(linhaCard, "Totais por Conta Contábil", totais.totalOrcadoPorContaContabil, totais.totalRealizadoPorContaContabil, totais.totalRealizadoPorContaContabilAnterior);
    linhaCard = renderTabelaTotais(linhaCard, "Totais por Responsável", totais.totalOrcadoPorResponsavel, totais.totalRealizadoPorResponsavel, totais.totalRealizadoPorResponsavelAnterior);
    linhaCard = renderTabelaTotais(linhaCard, "Totais por Filial", totais.totalOrcadoPorFilial, totais.totalRealizadoPorFilial, totais.totalRealizadoPorFilialAnterior);
    linhaCard = renderTabelaTotais(linhaCard, "Totais por Tipo", totais.totalOrcadoPorTipo, totais.totalRealizadoPorTipo, totais.totalRealizadoPorTipoAnterior);
    renderTabelaTotais(linhaCard, "Totais por Grupo", totais.totalOrcadoPorGrupo, totais.totalRealizadoPorGrupo, totais.totalRealizadoPorGrupoAnterior);

    return ws;
}

// =============================================================================
// ABA 2 - BPs (List<BaseBpDTO>)
// =============================================================================

function criarAbaBps(workbook, bps = [], mesesSelecionados = []) {
    const COLS_FIXAS = ["Filial", "Grupo", "Tipo Conta", "Conta Reduzido", "Conta Descrição", "C.Custo Código", "C.Custo Descrição", "Responsável"];
    const LARGURAS_FIXAS = [18, 16, 14, 14, 34, 14, 24, 20];

    const mesesFiltrados = NOMES_MESES
        .map((nome, idx) => ({ nome, idx }))
        .filter(({ idx }) => mesesSelecionados.length === 0 || mesesSelecionados.includes(idx + 1));

    const totalColunas = COLS_FIXAS.length + mesesFiltrados.length * 3 + 3; // + Total (Orçado/Realizado/Saldo)

    const ws = workbook.addWorksheet("BPs", {
        views: [{ state: "frozen", xSplit: 3, ySplit: 2 }],
    });

    ws.columns = Array.from({ length: totalColunas }, (_, i) => ({
        width: i < COLS_FIXAS.length ? LARGURAS_FIXAS[i] : 13,
    }));

    const linha1 = ws.getRow(1);
    const linha2 = ws.getRow(2);
    linha1.height = 28;
    linha2.height = 22;

    COLS_FIXAS.forEach((nome, i) => {
        const col = i + 1;
        ws.mergeCells(1, col, 2, col);
        const cell = ws.getCell(1, col);
        cell.value = nome;
        cell.style = estiloCabecalho(CORES.fixo.forte, 12);
        cell.border = { top: bordaFina(), left: bordaFina(), right: bordaFina(), bottom: { style: "medium", color: { argb: "FFFFFFFF" } } };
    });

    let col = COLS_FIXAS.length + 1;
    const aplicarGrupo = (titulo, cores) => {
        ws.mergeCells(1, col, 1, col + 2);
        const cabecGrupo = ws.getCell(1, col);
        cabecGrupo.value = titulo;
        cabecGrupo.style = estiloCabecalho(cores.forte, 12);

        ["Orçado", "Realizado", "Saldo"].forEach((sub, i) => {
            const c = ws.getCell(2, col + i);
            c.value = sub;
            c.style = estiloCabecalho(cores.clara, 10);
        });

        const inicio = col;
        col += 3;
        return inicio;
    };

    mesesFiltrados.forEach(({ nome }) => aplicarGrupo(nome, CORES.mes));
    const inicioTotal = aplicarGrupo("Total", CORES.total);

    const totaisGerais = {
        meses: mesesFiltrados.map(() => ({ orcado: 0, realizado: 0 })),
        orcado: 0, realizado: 0,
    };

    bps.forEach((bp) => {
        const linha = [
            `${bp.filialNome || ""}${bp.filialCodigo ? ` (${bp.filialCodigo})` : ""}`,
            bp.grupoDescricao || "",
            bp.contaTipo || "",
            bp.contaReduzido || "",
            (bp.contaDescricao || "").trim(),
            bp.centroCustoCodigo || "",
            bp.centroCustoDescricao || "",
            bp.ResponsavelNome || bp.responsavelNome || "",
        ];

        mesesFiltrados.forEach(({ nome }, i) => {
            const orcado = n((bp.valoresMensais || {})[nome]);
            const realizado = n((bp.despesasMensaisAtual || {})[nome]);
            linha.push(orcado, realizado, orcado - realizado);
            totaisGerais.meses[i].orcado += orcado;
            totaisGerais.meses[i].realizado += realizado;
        });

        const totalOrcado = n(bp.valorTotal);
        const totalRealizado = n(bp.valorTotalRealizadoAtual ?? bp.valorTotalRealizado);
        linha.push(totalOrcado, totalRealizado, totalOrcado - totalRealizado);
        totaisGerais.orcado += totalOrcado;
        totaisGerais.realizado += totalRealizado;

        const row = ws.addRow(linha);
        row.height = 15.5;
        row.eachCell((cell, colNumber) => {
            cell.font = { name: "Calibri", size: 9 };
            if (colNumber <= COLS_FIXAS.length) {
                cell.alignment = { horizontal: "left", vertical: "middle" };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.linhaFixa } };
            } else {
                cell.alignment = { horizontal: "right", vertical: "middle" };
                cell.numFmt = FORMATO_MOEDA;
                if (colNumber >= inicioTotal) {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.total.linha } };
                }
            }
        });
    });

    const linhaTotal = [
        "TOTAL GERAL", "", "", "", "", "", "", "",
        ...totaisGerais.meses.flatMap((m) => [m.orcado, m.realizado, m.orcado - m.realizado]),
        totaisGerais.orcado, totaisGerais.realizado, totaisGerais.orcado - totaisGerais.realizado,
    ];
    const rowTotal = ws.addRow(linhaTotal);
    rowTotal.height = 18;
    ws.mergeCells(rowTotal.number, 1, rowTotal.number, COLS_FIXAS.length);
    rowTotal.eachCell((cell, colNumber) => {
        cell.font = { name: "Calibri", size: 10, bold: true };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.totalGeral } };
        cell.border = { top: { style: "medium", color: { argb: "FF2C3E50" } } };
        cell.alignment = colNumber <= COLS_FIXAS.length ? { horizontal: "left", vertical: "middle" } : { horizontal: "right", vertical: "middle" };
        if (colNumber > COLS_FIXAS.length) cell.numFmt = FORMATO_MOEDA;
    });

    return ws;
}

// =============================================================================
// ABA 3 - DESPESAS (List<DespesaDTO>)
// =============================================================================

function criarAbaDespesas(workbook, despesas = []) {
    const ws = workbook.addWorksheet("Despesas", {
        views: [{ state: "frozen", ySplit: 1 }],
    });

    const colunas = [
        { header: "Data", key: "data", width: 12 },
        { header: "Filial", key: "filial", width: 20 },
        { header: "Grupo", key: "grupo", width: 16 },
        { header: "Conta Reduzido", key: "contaReduzido", width: 14 },
        { header: "Conta Descrição", key: "contaDescricao", width: 34 },
        { header: "C.Custo Cód", key: "centroCustoCodigo", width: 14 },
        { header: "Centro de Custo", key: "centroCusto", width: 24 },
        { header: "Tipo", key: "tipo", width: 14 },
        { header: "Responsável", key: "responsavel", width: 20 },
        { header: "Histórico", key: "historico", width: 40 },
        { header: "Valor", key: "valor", width: 15 },
    ];

    ws.columns = colunas.map(({ header, key, width }) => ({ header, key, width }));
    ws.getRow(1).height = 24;
    ws.getRow(1).eachCell((cell) => {
        cell.style = estiloCabecalho(CORES.fixo.forte, 10);
    });

    despesas.forEach((d, idx) => {
        const row = ws.addRow({
            data: d.data ? new Date(d.data) : null,
            filial: d.filialNome || "",
            grupo: d.grupoDescricao || "",
            contaReduzido: d.contaContabilReduzido || "",
            contaDescricao: (d.contaContabilDescricao || "").trim(),
            centroCustoCodigo: d.centroCustoCodigo || "",
            centroCusto: d.centroCustoDescricao || "",
            tipo: d.contaContabilTipo || "",
            responsavel: d.responsavelNome || "",
            historico: d.historico || "",
            valor: n(d.valor),
        });
        row.height = 15.5;
        row.getCell("data").numFmt = "dd/mm/yyyy";
        row.getCell("valor").numFmt = FORMATO_MOEDA;
        row.eachCell((cell) => {
            cell.font = { name: "Calibri", size: 9 };
            cell.alignment = { vertical: "middle" };
            if (idx % 2 === 0) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORES.linhaFixa } };
        });
        row.getCell("valor").alignment = { horizontal: "right", vertical: "middle" };
    });

    if (despesas.length > 0) {
        ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: colunas.length } };
    }

    return ws;
}

// =============================================================================
// EXPORT PRINCIPAL
// =============================================================================

/**
 * Gera o Excel completo a partir do RelatorioBPDTO retornado por /relatorio/excel.
 * @param {Object} relatorio - RelatorioBPDTO { bpDTO, despesaDTO, bpKpiDTO, totais }
 * @param {Object} opcoes
 * @param {string|number} opcoes.ano
 * @param {number[]} [opcoes.mesesSelecionados] - filtro de meses (1-12) aplicado só na aba BPs
 */
export async function exportarRelatorioBPExcel(relatorio = {}, { ano, mesesSelecionados = [] } = {}) {
    const { bpDTO = [], despesaDTO = [], bpKpiDTO = {}, totais = {} } = relatorio;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Sistema BP";
    workbook.created = new Date();

    criarAbaDashboard(workbook, bpKpiDTO, totais, ano);
    criarAbaBps(workbook, bpDTO, mesesSelecionados);
    criarAbaDespesas(workbook, despesaDTO);

    const excelBuffer = await workbook.xlsx.writeBuffer();
    const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(fileData, `relatorio_bp_${ano}_${new Date().getTime()}.xlsx`);
}